import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { AlertTriangle, Plus, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SEVERITY_COLORS = { low: 'text-blue-400 bg-blue-500/10', medium: 'text-amber-400 bg-amber-500/10', high: 'text-orange-400 bg-orange-500/10', critical: 'text-red-400 bg-red-500/10' };
const STATUS_COLORS = { open: 'text-amber-400 bg-amber-500/10', investigating: 'text-blue-400 bg-blue-500/10', resolved: 'text-emerald-400 bg-emerald-500/10', closed: 'text-muted-foreground bg-muted' };

export default function IncidentReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', incident_type: 'general', severity: 'low', actions_taken: '' });
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/incident-reports`, { withCredentials: true });
      setReports(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.description) return toast.error('Title and description required');
    try {
      await axios.post(`${API_URL}/api/incident-reports`, form, { withCredentials: true });
      toast.success('Incident report filed');
      setDialogOpen(false);
      setForm({ title: '', description: '', incident_type: 'general', severity: 'low', actions_taken: '' });
      fetchReports();
    } catch (err) { toast.error('Failed'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/incident-reports/${id}`, { status }, { withCredentials: true });
      toast.success('Status updated');
      fetchReports();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="incident-reports-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <AlertTriangle className="w-7 h-7 inline mr-3 text-orange-400" />Incident Reports
            </h1>
            <p className="text-muted-foreground mt-1">File and track incident reports</p>
          </div>
          <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="file-incident-btn"><Plus className="w-4 h-4 mr-2" />File Report</Button>
        </div>

        {reports.length === 0 ? (
          <div className="text-center py-16"><AlertTriangle className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No incident reports</p></div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => (
              <Card key={r.id} className="glass-card border-border" data-testid={`incident-${r.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-foreground font-medium text-sm">{r.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${SEVERITY_COLORS[r.severity]}`}>{r.severity}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{r.description}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Filed by {r.reported_by_name} - {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => { setSelectedReport(r); setDetailDialog(true); }}><Eye className="w-4 h-4" /></Button>
                    {isAdmin && r.status === 'open' && (
                      <Button size="sm" variant="outline" className="border-border text-muted-foreground text-xs" onClick={() => updateStatus(r.id, 'investigating')}>Investigate</Button>
                    )}
                    {isAdmin && r.status === 'investigating' && (
                      <Button size="sm" className="bg-emerald-600 text-white text-xs" onClick={() => updateStatus(r.id, 'resolved')}>Resolve</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="text-foreground">File Incident Report</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><label className="text-xs text-muted-foreground">Title</label>
                <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="incident-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Type</label>
                  <Select value={form.incident_type} onValueChange={v => setForm(p => ({...p, incident_type: v}))}>
                    <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass-card border-border">
                      <SelectItem value="general">General</SelectItem><SelectItem value="fall">Fall</SelectItem>
                      <SelectItem value="medication">Medication Error</SelectItem><SelectItem value="injury">Injury</SelectItem>
                      <SelectItem value="behavioral">Behavioral</SelectItem><SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-muted-foreground">Severity</label>
                  <Select value={form.severity} onValueChange={v => setForm(p => ({...p, severity: v}))}>
                    <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass-card border-border">
                      <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><label className="text-xs text-muted-foreground">Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[100px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" data-testid="incident-description" /></div>
              <div><label className="text-xs text-muted-foreground">Actions Taken</label>
                <textarea value={form.actions_taken} onChange={e => setForm(p => ({...p, actions_taken: e.target.value}))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" /></div>
              <Button className="w-full btn-primary" onClick={handleSubmit} data-testid="submit-incident-btn">Submit Report</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="text-foreground">Incident Details</DialogTitle></DialogHeader>
            {selectedReport && (
              <div className="space-y-3">
                <div className="flex gap-2"><span className={`text-xs px-2 py-0.5 rounded-full ${SEVERITY_COLORS[selectedReport.severity]}`}>{selectedReport.severity}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[selectedReport.status]}`}>{selectedReport.status}</span></div>
                <div><p className="text-xs text-muted-foreground">Description</p><p className="text-sm text-foreground">{selectedReport.description}</p></div>
                {selectedReport.actions_taken && <div><p className="text-xs text-muted-foreground">Actions Taken</p><p className="text-sm text-foreground">{selectedReport.actions_taken}</p></div>}
                <p className="text-xs text-muted-foreground">Filed: {new Date(selectedReport.created_at).toLocaleString()}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
